import { FormSection } from 'redux-form';
import { useRef } from 'react';
import PropTypes from 'prop-types';
import { css, etiColors, mediaQueries } from '@eti/styles';
import SeatMapTraveler from '../form-fields/SeatMapTraveler';
import { getTravelerIndexInArrayById, isFirstTraveler, isLastTraveler } from '../utils/travelers';
import { TRAVELER_ACTIVE_BUTTON_MIN_WIDTH, TRAVELER_ACTIVE_BUTTON_WIDE_MIN_WIDTH, TRAVELER_BUTTON_WIDTH } from '../utils/constants';
import SwitchTravelerButton from './SwitchTravelerButton';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const listWrapperStyles = navigationAvailable => css`
  display: flex;
  flex-direction: row;
  ${navigationAvailable && `box-shadow: inset 6px 0 6px -6px ${etiColors.grayScale[6]},
    inset -6px 0 6px -6px ${etiColors.grayScale[6]}`};
  overflow: hidden;
  width: 100%;

  @media ${mediaQueries.medium.up} {
    box-shadow: none;
    max-width: 750px;
  }

  @media ${mediaQueries.large.up} {
    max-width: 970px;
  }

  @media ${mediaQueries.xlarge.up} {
    max-width: 1170px;
  }
`;
const getListProps = navigationAvailable => !navigationAvailable ? `width: auto;` : '';
const listStyles = (listOffset, navigationAvailable) => css`
  display: flex;
  flex-direction: row;
  ${navigationAvailable ? `margin-left: ${listOffset};` : 'margin: auto;'}

  @media ${mediaQueries.large.up} {
    ${getListProps(navigationAvailable)};
  }

  > li {
    border-left: 1px dotted ${etiColors.grayScale[6]};
    border-right: 1px dotted ${etiColors.grayScale[6]};
    margin: 0 -1px 0 0;
  }
`;
const SeatMapTravelersStyles = css`
  align-items: center;
  display: flex;
  flex-direction: row;
`;
const SeatMapTravelersWrapperStyles = css`
  @media ${mediaQueries.medium.up} {
    padding: 0;
  }

  @media ${mediaQueries.large.up} {
    padding: 0 15px;
  }
`;
const SeatMapTravelers = _ref => {
  let {
    active = [],
    onChange,
    onRemoveSeat,
    selectedSeats,
    travelers = [],
    onNextTravelerClick,
    onPreviousTravelerClick
  } = _ref;
  const seatMapTravelersRef = useRef();
  const seatMapTravelersListRef = useRef();
  const seatMapTravelersWrapperRef = useRef();
  const getCountOfTravelersFitTheSection = (section, activeButtonMinWidth) => (section.offsetWidth - activeButtonMinWidth) / TRAVELER_BUTTON_WIDTH + 1;
  const isNavigationAvailable = (() => {
    let isAvailable = true;
    const section = seatMapTravelersRef.current;
    if (section) {
      const travelersCountFitSection = getCountOfTravelersFitTheSection(section, TRAVELER_ACTIVE_BUTTON_MIN_WIDTH);
      isAvailable = travelers.length > travelersCountFitSection;
    }
    return isAvailable;
  })();
  const shouldUpdateTravelersListOffset = activeTravelerIndex => {
    const section = seatMapTravelersWrapperRef.current;
    if (section) {
      const travelersInSectionWithoutScroll = getCountOfTravelersFitTheSection(section, TRAVELER_ACTIVE_BUTTON_WIDE_MIN_WIDTH);
      return travelers.length - activeTravelerIndex >= Math.trunc(travelersInSectionWithoutScroll);
    }
    return false;
  };
  const getTravelersListComputedOffset = () => {
    if (seatMapTravelersListRef.current) {
      const compStyles = window.getComputedStyle(seatMapTravelersListRef.current);
      return compStyles.getPropertyValue('margin-left');
    }
    return 0;
  };
  const getTravelersListOffset = () => {
    if (!isNavigationAvailable) {
      return 0;
    }
    const activeTravelerIndexInArray = getTravelerIndexInArrayById(travelers, active[1]);
    if (shouldUpdateTravelersListOffset(activeTravelerIndexInArray)) {
      const offset = activeTravelerIndexInArray * TRAVELER_BUTTON_WIDTH;
      return `-${offset}px`;
    }
    return getTravelersListComputedOffset();
  };
  return /*#__PURE__*/_jsx("div", {
    className: SeatMapTravelersWrapperStyles,
    "data-testid": "seatMap-travelers",
    ref: seatMapTravelersRef,
    children: /*#__PURE__*/_jsxs(FormSection, {
      className: SeatMapTravelersStyles,
      name: String(active[0]),
      children: [isNavigationAvailable && /*#__PURE__*/_jsx(SwitchTravelerButton, {
        disabled: isFirstTraveler(travelers, active[1]),
        navigateTo: "previous",
        onClick: onPreviousTravelerClick
      }), /*#__PURE__*/_jsx("div", {
        className: listWrapperStyles(isNavigationAvailable),
        ref: seatMapTravelersWrapperRef,
        children: /*#__PURE__*/_jsx("ul", {
          className: listStyles(getTravelersListOffset(), isNavigationAvailable),
          ref: seatMapTravelersListRef,
          children: travelers.map((traveler, index) => /*#__PURE__*/_jsx(SeatMapTraveler, {
            active: active,
            "data-testid": `seatMap-travelers-${traveler.id}`,
            fieldName: `traveler${traveler.id}`,
            navigationAvailable: isNavigationAvailable,
            onChange: onChange,
            onRemoveSeat: onRemoveSeat(active[0], traveler.id),
            selectedSeats: selectedSeats,
            travelerIndex: index,
            ...traveler
          }, traveler.id))
        })
      }), isNavigationAvailable && /*#__PURE__*/_jsx(SwitchTravelerButton, {
        disabled: isLastTraveler(travelers, active[1]),
        navigateTo: "next",
        onClick: onNextTravelerClick
      })]
    })
  });
};
SeatMapTravelers.propTypes = {
  active: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  onChange: PropTypes.func,
  onNextTravelerClick: PropTypes.func,
  onPreviousTravelerClick: PropTypes.func,
  onRemoveSeat: PropTypes.func,
  selectedSeats: PropTypes.oneOfType([PropTypes.objectOf(PropTypes.any), PropTypes.arrayOf(PropTypes.any)]),
  travelers: PropTypes.arrayOf(PropTypes.shape({
    ageType: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string
  }))
};
export default SeatMapTravelers;
//# sourceMappingURL=SeatMapTravelers.js.map